import { Component, computed, DestroyRef, ElementRef, inject, output, signal } from '@angular/core';
import { IsActiveMatchOptions, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { PrimaryNavItem } from '../../models';
import { APP_NAV_ITEMS } from '../../constants';
import { CapitalizePipe } from '../../pipes';
import { RcNavigateDirective } from '../../directives';
import { RouteContextMenuService } from '../route-context-menu';

@Component({
  selector: 'rc-app-menu',
  standalone: true,
  templateUrl: './app-menu.component.html',
  styleUrl: './app-menu.component.scss',
  imports: [TranslateModule, CapitalizePipe, RcNavigateDirective],
})
export class AppMenuComponent {
  close = output<void>();

  readonly router = inject(Router);
  readonly #routeMenu = inject(RouteContextMenuService);
  readonly navItems: PrimaryNavItem[] = APP_NAV_ITEMS;

  /** The chain of hovered items, one per expanded column (index 0 = top level). */
  hoveredPath = signal<PrimaryNavItem[]>([]);

  /** The columns to render: the root list plus one list per hovered item that has children. */
  columns = computed<PrimaryNavItem[][]>(() => {
    const cols: PrimaryNavItem[][] = [this.navItems];
    for (const item of this.hoveredPath()) {
      if (!item.children?.length) break;
      cols.push(item.children);
    }
    return cols;
  });

  #host: ElementRef<HTMLElement> = inject(ElementRef);

  // "menu-aim" safe-triangle. Submenus open to the right (flex row), and their
  // items sit at the top of the column, so the path to a bottom item's submenu
  // is a diagonal up-and-right through the sibling column. We keep a short
  // window of recent cursor positions and, while the movement stays aimed at the
  // open submenu's right-edge corners, defer switching — regardless of speed,
  // direction (up/down-right) or a brief pause.
  private readonly RECHECK_MS = 100;
  private readonly LOC_WINDOW_MS = 250;
  private switchTimer?: ReturnType<typeof setTimeout>;
  private mouseLocs: { x: number; y: number; t: number }[] = [];

  private readonly matchOpts: IsActiveMatchOptions = {
    paths: 'subset',
    queryParams: 'ignored',
    fragment: 'ignored',
    matrixParams: 'ignored',
  };

  constructor() {
    inject(DestroyRef).onDestroy(() => clearTimeout(this.switchTimer));
    // Set after all fields (incl. matchOpts) are initialized — buildActivePath
    // relies on isActive, which reads matchOpts.
    this.hoveredPath.set(this.buildActivePath(this.navItems));
  }

  /** True when the item's own link, or any descendant's link, matches the current route. */
  isActive(item: PrimaryNavItem): boolean {
    if (item.link && this.router.isActive(item.link, this.matchOpts)) return true;
    return (item.children ?? []).some((child) => this.isActive(child));
  }

  /** Record recent cursor positions (last LOC_WINDOW_MS) for the aim heuristic. */
  onMouseMove(event: MouseEvent) {
    const now = Date.now();
    this.mouseLocs.push({ x: event.clientX, y: event.clientY, t: now });
    const cutoff = now - this.LOC_WINDOW_MS;
    while (this.mouseLocs.length > 2 && this.mouseLocs[0].t < cutoff) this.mouseLocs.shift();
  }

  /** Hovering an item in column `level` collapses deeper columns and expands this one. */
  setHovered(item: PrimaryNavItem, level: number) {
    clearTimeout(this.switchTimer);
    // Already the hovered item at this level — nothing to do.
    if (this.hoveredPath()[level] === item) return;

    const wouldCollapseOpenSubmenu = this.columns().length > level + 1;

    // Nothing is expanded beyond this level (first open / going deeper / hovering
    // within the deepest column) → apply immediately, no delay (case 3).
    if (!wouldCollapseOpenSubmenu) {
      this.applyHovered(item, level);
      return;
    }

    // A submenu is open beyond this level and this hover would collapse/switch
    // it. Defer while the cursor is aiming at the submenu; commit once it isn't.
    this.possiblyActivate(item, level);
  }

  /**
   * Re-checks on a short interval (always deferring the first decision so a real
   * movement vector can form): while the cursor is inside the open submenu, or
   * still aiming at it, the switch is postponed; once it clearly isn't, apply it.
   */
  private possiblyActivate(item: PrimaryNavItem, level: number) {
    clearTimeout(this.switchTimer);
    this.switchTimer = setTimeout(() => {
      if (this.shouldKeepSubmenuOpen()) this.possiblyActivate(item, level);
      else this.applyHovered(item, level);
    }, this.RECHECK_MS);
  }

  /** Keep the open submenu if the cursor is inside it, or still heading toward it. */
  private shouldKeepSubmenuOpen(): boolean {
    const rect = this.submenuRect();
    const cur = this.mouseLocs[this.mouseLocs.length - 1];
    if (!rect || !cur) return false;

    // Already within the submenu column (incl. its empty area) → always keep.
    if (cur.x >= rect.left && cur.x <= rect.right && cur.y >= rect.top && cur.y <= rect.bottom) {
      return true;
    }
    return this.movingTowardSubmenu(rect);
  }

  /**
   * True when the recent cursor movement stays aimed at the open submenu — i.e.
   * within the triangle formed by the current position and the submenu's far
   * (right-edge) corners. Window-based, so speed/pauses/up-down don't fool it.
   */
  private movingTowardSubmenu(rect: DOMRect): boolean {
    if (this.mouseLocs.length < 2) return false;

    const cur = this.mouseLocs[this.mouseLocs.length - 1];
    const prev = this.mouseLocs[0];
    const slope = (a: { x: number; y: number }, b: { x: number; y: number }) => (b.y - a.y) / (b.x - a.x);

    const upperRight = { x: rect.right, y: rect.top };
    const lowerRight = { x: rect.right, y: rect.bottom };

    return slope(cur, upperRight) < slope(prev, upperRight) && slope(cur, lowerRight) > slope(prev, lowerRight);
  }

  /** Bounding box of the deepest open submenu column, or null if none is open. */
  private submenuRect(): DOMRect | null {
    const cols = this.#host.nativeElement.querySelectorAll<HTMLElement>('.app-menu__col');
    return cols.length < 2 ? null : cols[cols.length - 1].getBoundingClientRect();
  }

  private applyHovered(item: PrimaryNavItem, level: number) {
    const next = this.hoveredPath().slice(0, level);
    next[level] = item;
    this.hoveredPath.set(next);
  }

  clearHovered() {
    // The route context menu renders outside .app-menu, so opening/using it fires
    // mouseleave here — don't tear the menu down while it's open.
    if (this.#routeMenu.isOpen()) return;
    clearTimeout(this.switchTimer);
    this.hoveredPath.set([]);
  }

  onItemClick(item: PrimaryNavItem) {
    clearTimeout(this.switchTimer);
    if (item.children?.length) return;
    if (item.link) this.navigate(item.link);
  }

  navigate(link: string) {
    this.router.navigate([link]);
    this.close.emit();
  }

  colClass(colIndex: number): string {
    const base = 'app-menu__col';
    return colIndex === 0 ? `${base} ${base}--primary` : `${base} ${base}--level-${colIndex}`;
  }

  /** Builds the path of ancestor items leading to the currently active route. */
  private buildActivePath(items: PrimaryNavItem[]): PrimaryNavItem[] {
    for (const item of items) {
      if (item.children?.length && this.isActive(item)) {
        return [item, ...this.buildActivePath(item.children)];
      }
    }
    return [];
  }
}
